/**
 * Portfolio API Service
 * Handles all API communication between the website and the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ---------------------------------------------------------------------------
// In-memory response cache for public read-only endpoints
// Prevents duplicate network requests when navigating between pages.
// TTL: 5 minutes for portfolio data (content changes rarely).
// ---------------------------------------------------------------------------
const _cache = new Map(); // key → { data, expiresAt }
const _CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let _forceReload = false; // bypass browser HTTP cache on next fetch after a mutation
const EDIT_MODE_KEY = 'cms_edit_mode';

function _cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) { _cache.delete(key); return null; }
  return entry.data;
}

function _cacheSet(key, data) {
  _cache.set(key, { data, expiresAt: Date.now() + _CACHE_TTL_MS });
}

function isEditSessionActive() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('edit') === 'true' ||
      window.localStorage.getItem(EDIT_MODE_KEY) === 'true'
    );
  } catch (error) {
    return false;
  }
}

/**
 * Invalidate all cached portfolio data (call after edit-mode mutations).
 * Also sets a flag so the next network fetch bypasses the browser HTTP cache
 * (which would otherwise serve the stale Cache-Control: max-age=300 response).
 */
export function invalidatePortfolioCache() {
  _cache.clear();
  _forceReload = true;
}

/**
 * Get CSRF token from cookies
 * @returns {string|null} - CSRF token or null if not found
 */
const getCsrfToken = () => {
  const name = 'csrf_token';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(name + '=')) {
      const token = cookie.substring(name.length + 1);
      return token;
    }
  }
  
  return null;
};

/**
 * Get headers with authentication and CSRF token
 * @param {string} token - Authentication token (optional)
 * @param {boolean} includeContentType - Include Content-Type header (default: true)
 * @returns {Object} - Headers object
 */
const getHeaders = (token = null, includeContentType = true) => {
  const headers = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add CSRF token for state-changing requests
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  
  return headers;
};

/**
 * Map language code to language ID
 * @param {string} languageCode - Language code ('en', 'es')
 * @returns {number} - Language ID
 */
const getLanguageId = (languageCode) => {
  const languageMap = {
    'en': 1,
    'es': 2
  };
  return languageMap[languageCode] || 1;
};

/**
 * Handles API responses and errors
 * @param {Response} response - Fetch response object
 * @returns {Promise<any>} - Parsed JSON data
 * @throws {Error} - If response is not ok
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      detail: `HTTP error! status: ${response.status}`
    }));
    throw new Error(error.detail || 'An error occurred');
  }
  
  // Check if response has content
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (jsonError) {
      // Return a basic success object if JSON parsing fails
      return { success: true };
    }
  }
  
  // If no JSON content, return success indicator
  return { success: true };
};

/**
 * Portfolio API methods
 */
export const portfolioApi = {
  /**
   * Authentication Methods
   */

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Access token and user info
   */
  login: async (email, password) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  /**
   * Verify authentication token
   * @param {string} token - Authentication token
   * @returns {Promise<boolean>} - True if token is valid
   */
  verifyToken: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  },

  /**
   * Get current user information
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - User data with permissions
   */
  getCurrentUser: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  /**
   * Get all experiences (for edit mode - selecting experiences to add)
   * Uses public website endpoint that doesn't require authentication
   * @param {number} page - Page number (default 1)
   * @param {number} pageSize - Page size (default 100)
   * @param {string} token - Authentication token (optional, not used for public endpoint)
   * @returns {Promise<Object>} - List of experiences
   */
  getAllExperiences: async (page = 1, pageSize = 100, token = null) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/website/experiences?page=${page}&page_size=${pageSize}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Return in expected format (the endpoint returns array directly)
      return { items: Array.isArray(data) ? data : [] };
    } catch (error) {
      console.error('Error fetching experiences:', error);
      throw error;
    }
  },  /**
   * Public API Methods
   */

  /**
   * Fetch the default portfolio with specified language
   * @param {string} languageCode - Language code (e.g., 'en', 'es')
   * @returns {Promise<Object>} - Portfolio data
   */
  getDefaultPortfolio: async (languageCode = 'en') => {
    const cacheKey = `default-portfolio:${languageCode}`;
    const bypassHttpCache = _forceReload || isEditSessionActive();
    _forceReload = false;

    if (!bypassHttpCache) {
      const cached = _cacheGet(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/website/default?language_code=${languageCode}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          cache: bypassHttpCache ? 'reload' : 'no-cache',
        }
      );
      const data = await handleResponse(response);
      _cacheSet(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching default portfolio:', error);
      throw error;
    }
  },

  /**
   * Fetch a specific portfolio by ID with specified language
   * @param {number} portfolioId - Portfolio ID
   * @param {string} languageCode - Language code (e.g., 'en', 'es')
   * @returns {Promise<Object>} - Portfolio data
   */
  getPortfolio: async (portfolioId, languageCode = 'en') => {
    const cacheKey = `portfolio:${portfolioId}:${languageCode}`;
    const bypassHttpCache = _forceReload || isEditSessionActive();
    _forceReload = false;

    if (!bypassHttpCache) {
      const cached = _cacheGet(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/website/portfolios/${portfolioId}/public?language_code=${languageCode}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: bypassHttpCache ? 'reload' : 'no-cache',
        }
      );
      const data = await handleResponse(response);
      _cacheSet(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching portfolio ${portfolioId}:`, error);
      throw error;
    }
  },

  /**
   * Chat with the default agent configured for a portfolio.
   * @param {number} portfolioId - Portfolio ID
   * @param {string} message - User message
   * @param {number|null} sessionId - Existing session ID for continuing conversation
   * @param {string} languageCode - Language code (en, es)
   * @returns {Promise<Object>} - Chat response from backend
   */
  chatWithPortfolioAgent: async (portfolioId, message, sessionId = null, languageCode = 'en') => {
    try {
      const payload = {
        message,
        session_id: sessionId,
        language_id: getLanguageId(languageCode),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/website/chat/portfolios/${portfolioId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      return await handleResponse(response);
    } catch (error) {
      console.error('Error chatting with portfolio agent:', error);
      throw error;
    }
  },

  /**
   * CMS Operations (require authentication)
   */

  /**
   * Update project text content
   * @param {number} textId - Project text ID
   * @param {Object} content - Content to update (name, description)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated project text
   */
  updateProjectText: async (textId, content, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/project-text/${textId}`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(content),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating project text ${textId}:`, error);
      throw error;
    }
  },

  /**
   * Update experience text content
   * @param {number} textId - Experience text ID
   * @param {Object} content - Content to update (name, description)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated experience text
   */
  updateExperienceText: async (textId, content, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/experience-text/${textId}`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(content),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating experience text ${textId}:`, error);
      throw error;
    }
  },

  /**
   * Update section text content
   * @param {number} textId - Section text ID
   * @param {Object} content - Content to update (text)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated section text
   */
  updateSectionText: async (textId, content, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/section-text/${textId}`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(content),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating section text ${textId}:`, error);
      throw error;
    }
  },

  /**
   * Upload an image for a content entity
   * @param {File} file - Image file to upload
   * @param {string} entityType - Entity type ('portfolio', 'project', 'experience')
   * @param {number} entityId - Entity ID
   * @param {string} category - Image category ('main', 'thumbnail', 'gallery', 'background')
   * @param {string} token - Authentication token
   * @param {string} languageCode - Language code ('en', 'es') - optional
   * @returns {Promise<Object>} - Uploaded image details
   */
  uploadImage: async (file, entityType, entityId, category, token, languageCode = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = getHeaders(token, false); // Don't include Content-Type for FormData
      
      // Build query string with language_id if provided
      const queryParams = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId,
        category: category
      });
      
      if (languageCode) {
        const languageId = getLanguageId(languageCode);
        queryParams.append('language_id', languageId);
      }
      

      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/images?${queryParams.toString()}`,
        {
          method: 'POST',
          headers: headers,
          credentials: 'include',
          body: formData,
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  /**
   * Fetch existing images for a content entity (portfolio, project, experience, section)
   * @param {string} entityType - Entity type
   * @param {number|string} entityId - Entity identifier (experience text ID allowed)
   * @param {Object} options - Optional filters (category, languageCode, languageId)
   * @param {string} token - Authentication token
   * @returns {Promise<Object|Array>} - Image list response
   */
  getContentImages: async (entityType, entityId, options = {}, token) => {
    try {
      if (!entityType || entityId === undefined || entityId === null) {
        throw new Error('Entity type and entity ID are required to fetch images');
      }

      const queryParams = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId
      });

      if (options.category) {
        queryParams.append('category', options.category);
      }

      let languageId = options.languageId;
      if (!languageId && options.languageCode) {
        languageId = getLanguageId(options.languageCode);
      }

      if (languageId) {
        queryParams.append('language_id', languageId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/images?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );

      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching content images:', error);
      throw error;
    }
  },

  /**
   * Reorder content (projects, experiences, sections)
   * @param {string} entityType - Entity type ('project', 'experience', 'section')
   * @param {Array<number>} entityIds - Array of entity IDs in new order
   * @param {number} portfolioId - Portfolio ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Reorder confirmation
   */
  reorderContent: async (entityType, entityIds, portfolioId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/order`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify({
            entity_type: entityType,
            entity_ids: entityIds,
            portfolio_id: portfolioId,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error reordering content:', error);
      throw error;
    }
  },

  /**
   * Reorder sections within a specific project
   * @param {number} projectId - Project ID
   * @param {Array<number>} sectionIds - Array of section IDs in new order
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Reorder confirmation
   */
  reorderProjectSections: async (projectId, sectionIds, token) => {
    
    try {
      const url = `${API_BASE_URL}/api/cms/content/project/${projectId}/sections/order`;
      const body = JSON.stringify({ section_ids: sectionIds });
      
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(token),
        credentials: 'include',
        body: body,
      });
      
      
      const result = await handleResponse(response);
      return result;
    } catch (error) {
      console.error('[API] Error reordering project sections:', error);
      console.error('[API] Error stack:', error.stack);
      throw error;
    }
  },

  /**
   * Reorder content within a specific section (images, attachments)
   * @param {number} sectionId - Section ID
   * @param {Array<Object>} contentItems - Array of content items with type, id, display_order
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Reorder confirmation
   */
  reorderSectionContent: async (sectionId, contentItems, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/section/${sectionId}/content/order`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify({
            content_items: contentItems,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error reordering section content:', error);
      throw error;
    }
  },

  /**
   * Update portfolio metadata (name, etc.)
   * @param {number} portfolioId - Portfolio ID
   * @param {Object} data - Data to update (name, description, etc.)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated portfolio
   */
  updatePortfolio: async (portfolioId, data, token) => {
    
    let response;
    let fetchFailed = false;
    
    try {
      response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(data),
        }
      );
    } catch (fetchError) {
      fetchFailed = true;
      console.error('Fetch failed but request may have been processed:', fetchError);
      
      // Return a success object - the request was likely processed even if response failed
      // This is a workaround for CORS/network issues where request completes but response is blocked
      return { 
        success: true, 
        id: portfolioId,
        message: 'Update sent (response unavailable)',
        ...data 
      };
    }
    
    // If we got a response object, try to handle it
    try {
      const result = await handleResponse(response);
      return result;
    } catch (handleError) {
      console.error('Error handling response:', handleError);
      // If response was OK but parsing failed, treat as success
      if (response && response.ok) {
        return { success: true, id: portfolioId, ...data };
      }
      throw handleError;
    }
  },

  /**
   * Update project metadata (repository_url, website_url)
   * @param {number} projectId - Project ID
   * @param {Object} metadata - Metadata to update (repository_url, website_url)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated project metadata
   */
  updateProjectMetadata: async (projectId, metadata, token) => {
    try {
      const queryParams = new URLSearchParams();
      if (metadata.repository_url !== undefined) {
        queryParams.append('repository_url', metadata.repository_url);
      }
      if (metadata.website_url !== undefined) {
        queryParams.append('website_url', metadata.website_url);
      }

      const headers = getHeaders(token, false); // No body, so no Content-Type needed

      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/project/${projectId}?${queryParams.toString()}`,
        {
          method: 'PATCH',
          headers: headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating project ${projectId} metadata:`, error);
      throw error;
    }
  },

  /**
   * Update experience metadata (years)
   * @param {number} experienceId - Experience ID
   * @param {Object} metadata - Metadata to update (years)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated experience
   */
  updateExperienceMetadata: async (experienceId, metadata, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/experiences/${experienceId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(metadata),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating experience ${experienceId} metadata:`, error);
      throw error;
    }
  },

  /**
   * Create a new experience
   * @param {Object} experienceData - Experience data (code, years, icon, experience_texts)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created experience
   */
  createExperience: async (experienceData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/experiences/`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(experienceData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating experience:', error);
      throw error;
    }
  },

  /**
   * Delete an experience
   * @param {number} experienceId - Experience ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Deleted experience
   */
  deleteExperience: async (experienceId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/experiences/${experienceId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error deleting experience ${experienceId}:`, error);
      throw error;
    }
  },

  /**
   * Add experience to portfolio
   * @param {number} portfolioId - Portfolio ID
   * @param {number} experienceId - Experience ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated portfolio
   */
  addExperienceToPortfolio: async (portfolioId, experienceId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/experiences/${experienceId}`,
        {
          method: 'POST',
          headers: getHeaders(token, false), // No body, so no Content-Type needed
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding experience to portfolio:', error);
      throw error;
    }
  },

  /**
   * Remove experience from portfolio
   * @param {number} portfolioId - Portfolio ID
   * @param {number} experienceId - Experience ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated portfolio
   */
  removeExperienceFromPortfolio: async (portfolioId, experienceId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/experiences/${experienceId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error removing experience from portfolio:', error);
      throw error;
    }
  },

  /**
   * Update translation text
   * @param {number} translationId - Translation ID
   * @param {Object} content - Translation content (identifier, text, language_id)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated translation
   */
  updateTranslation: async (translationId, content, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/translations/${translationId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(content),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating translation ${translationId}:`, error);
      throw error;
    }
  },

  /**
   * Get translation by identifier and language
   * @param {string} identifier - Translation identifier (e.g., 'hero_tagline')
   * @param {string} languageCode - Language code (e.g., 'en', 'es')
   * @returns {Promise<Object>} - Translation object
   */
  getTranslationByIdentifier: async (identifier, languageCode = 'en') => {
    try {
      const languageId = getLanguageId(languageCode);
      const response = await fetch(
        `${API_BASE_URL}/api/translations/full?identifier=${identifier}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      const data = await handleResponse(response);
      
      // Filter by language ID from the results
      if (data.items && data.items.length > 0) {
        // Find the translation for the specific language
        const translation = data.items.find(item => {
          // Check if the translation has the matching language in its language array
          return item.language && item.language.some(lang => lang.id === languageId);
        });
        return translation || data.items[0]; // Fallback to first if language not found
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching translation ${identifier}:`, error);
      throw error;
    }
  },

  /**
   * Project-specific operations
   */

  /**
   * Create a new project
   * @param {Object} projectData - Project data (repository_url, website_url, project_texts, categories, skills)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created project
   */
  createProject: async (projectData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(projectData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  /**
   * Update an existing project
   * @param {number} projectId - Project ID
   * @param {Object} projectData - Project data to update (repository_url, website_url, project_texts, categories, skills)
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated project
   */
  updateProject: async (projectId, projectData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(projectData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a project
   * @param {number} projectId - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Deleted project
   */
  deleteProject: async (projectId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error deleting project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Add a project to a portfolio
   * @param {number} portfolioId - Portfolio ID
   * @param {number} projectId - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  addProjectToPortfolio: async (portfolioId, projectId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/projects/${projectId}`,
        {
          method: 'POST',
          headers: getHeaders(token, false), // No body, so no Content-Type needed
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding project to portfolio:', error);
      throw error;
    }
  },

  /**
   * Remove a project from a portfolio
   * @param {number} portfolioId - Portfolio ID
   * @param {number} projectId - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  removeProjectFromPortfolio: async (portfolioId, projectId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/projects/${projectId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error removing project from portfolio:', error);
      throw error;
    }
  },

  /**
   * Get all languages
   * @param {string} token - Authentication token (optional for public access)
   * @returns {Promise<Object>} - Languages list
   */
  getLanguages: async (token = null) => {
    try {
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };
      const response = await fetch(
        `${API_BASE_URL}/api/languages/?page=1&page_size=100`,
        {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching languages:', error);
      throw error;
    }
  },

  /**
   * Get all categories
   * @param {string} token - Authentication token (optional for public access)
   * @returns {Promise<Object>} - Categories list
   */
  getCategories: async (token = null) => {
    try {
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };
      const response = await fetch(
        `${API_BASE_URL}/api/categories/?page=1&page_size=100`,
        {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Update a portfolio link category
   */
  updateLinkCategory: async (categoryId, updateData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/categories/${categoryId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(updateData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating link category ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Get all skills
   * @param {string} token - Authentication token (optional for public access)
   * @returns {Promise<Object>} - Skills list
   */
  getAllSkills: async (token = null) => {
    try {
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };
      const response = await fetch(
        `${API_BASE_URL}/api/skills/?page=1&page_size=100`,
        {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching skills:', error);
      throw error;
    }
  },

  /**
   * Backward-compatible skills lookup used by edit-mode dialogs.
   * @param {string} token - Authentication token (optional for public access)
   * @returns {Promise<Object>} - Skills list
   */
  getSkills: async (token = null) => {
    return portfolioApi.getAllSkills(token);
  },

  /**
   * Add a skill to a project
   * @param {number} projectId - Project ID
   * @param {number} skillId - Skill ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  addSkillToProject: async (projectId, skillId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/skills/${skillId}`,
        {
          method: 'POST',
          headers: getHeaders(token, false),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding skill to project:', error);
      throw error;
    }
  },

  /**
   * Remove a skill from a project
   * @param {number} projectId - Project ID
   * @param {number} skillId - Skill ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  removeSkillFromProject: async (projectId, skillId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/skills/${skillId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error removing skill from project:', error);
      throw error;
    }
  },

  /**
   * Upload an image for a specific project
   * @param {number} projectId - Project ID
   * @param {File} file - Image file to upload
   * @param {string} category - Image category (e.g., 'screenshots', 'diagrams')
   * @param {string} token - Authentication token
   * @param {string} languageCode - Language code ('en', 'es') - optional
   * @returns {Promise<Object>} - Uploaded image details
   */
  uploadProjectImage: async (projectId, file, category, token, languageCode = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      if (languageCode) {
        const languageId = getLanguageId(languageCode);
        formData.append('language_id', languageId);
      }

      const headers = getHeaders(token, false); // Don't include Content-Type for FormData
      

      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/images`,
        {
          method: 'POST',
          headers: headers,
          credentials: 'include',
          body: formData,
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error uploading project image:', error);
      throw error;
    }
  },

  /**
   * Upload attachment (generic file upload)
   * @param {File} file - File to upload
   * @param {string} entityType - Entity type ('section', 'project', etc.)
   * @param {number} entityId - Entity ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Uploaded file details
   */
  uploadAttachment: async (file, entityType, entityId, token) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = getHeaders(token, false); // Don't include Content-Type for FormData

      // Build query string
      const queryParams = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId
      });


      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/attachments?${queryParams.toString()}`,
        {
          method: 'POST',
          headers: headers,
          credentials: 'include',
          body: formData,
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  },

  /**
   * Upload an attachment for a specific project
   * @param {number} projectId - Project ID
   * @param {File} file - Attachment file to upload
   * @param {number} categoryId - Category ID (optional)
   * @param {string} token - Authentication token
   * @param {string} languageCode - Language code ('en', 'es') - optional
   * @returns {Promise<Object>} - Uploaded attachment details
   */
  uploadProjectAttachment: async (projectId, file, categoryId, token, languageCode = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (categoryId) {
        formData.append('category_id', categoryId);
      }

      if (languageCode) {
        const languageId = getLanguageId(languageCode);
        formData.append('language_id', languageId);
      }

      const headers = getHeaders(token, false); // Don't include Content-Type for FormData
      

      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/attachments`,
        {
          method: 'POST',
          headers: headers,
          credentials: 'include',
          body: formData,
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error uploading project attachment:', error);
      throw error;
    }
  },

  // ===================================
  // Project Sections API
  // ===================================

  /**
   * Get all sections for a project
   * @param {number} projectId - Project ID
   * @param {string} token - Authentication token
   * @returns {Promise<Array>} - Array of sections
   */
  getProjectSections: async (projectId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/sections`,
        {
          method: 'GET',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting project sections:', error);
      throw error;
    }
  },

  /**
   * Add an existing section to a project
   * @param {number} projectId - Project ID
   * @param {number} sectionId - Section ID
   * @param {Object} data - Section association data {display_order}
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  addSectionToProject: async (projectId, sectionId, data, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/sections/${sectionId}`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(data),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding section to project:', error);
      throw error;
    }
  },

  /**
   * Create a new section and add it to a project
   * @param {number} projectId - Project ID
   * @param {Object} sectionData - Section data {code, section_texts, display_order}
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created section
   */
  createProjectSection: async (projectId, sectionData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/sections`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(sectionData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating project section:', error);
      throw error;
    }
  },

  /**
   * Remove a section from a project
   * @param {number} projectId - Project ID
   * @param {number} sectionId - Section ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  /**
   * Update a section
   * @param {number} sectionId - Section ID
   * @param {Object} sectionData - Section data to update
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated section
   */
  updateSection: async (sectionId, sectionData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sections/${sectionId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(sectionData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  },

  /**
   * Delete a section record.
   * @param {number} sectionId - Section ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Delete confirmation
   */
  deleteSection: async (sectionId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sections/${sectionId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting section:', error);
      throw error;
    }
  },

  removeSectionFromProject: async (projectId, sectionId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/sections/${sectionId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error removing section from project:', error);
      throw error;
    }
  },

  /**
   * Update display_order for a section within a specific project
   * @param {number} projectId - Project ID
   * @param {number} sectionId - Section ID
   * @param {number} displayOrder - New display order
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Update confirmation
   */
  updateSectionDisplayOrder: async (projectId, sectionId, displayOrder, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cms/content/project/${projectId}/section/${sectionId}/order`,
        {
          method: 'PATCH',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify({
            display_order: displayOrder,
          }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating section display order:', error);
      throw error;
    }
  },

  /**
   * Add an image to a section
   * @param {number} sectionId - Section ID
   * @param {Object} imageData - Image data {image_path, language_id, display_order}
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created image
   */
  addSectionImage: async (sectionId, imageData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/sections/${sectionId}/images`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(imageData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding section image:', error);
      throw error;
    }
  },

  /**
   * Delete a section image
   * @param {number} imageId - Image ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  deleteSectionImage: async (imageId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/sections/images/${imageId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting section image:', error);
      throw error;
    }
  },

  /**
   * Add an attachment to a section
   * @param {number} sectionId - Section ID
   * @param {Object} attachmentData - Attachment data {file_path, file_name, language_id, display_order}
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created attachment
   */
  addSectionAttachment: async (sectionId, attachmentData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/sections/${sectionId}/attachments`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(attachmentData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error adding section attachment:', error);
      throw error;
    }
  },

  /**
   * Delete a section attachment
   * @param {number} attachmentId - Attachment ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Success message
   */
  deleteSectionAttachment: async (attachmentId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/sections/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting section attachment:', error);
      throw error;
    }
  },

  /**
   * Send a contact form email
   * @param {Object} contactData - Contact form data {name, email, subject, message}
   * @returns {Promise<Object>} - Success response
   */
  sendContactEmail: async (contactData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/email/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(contactData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error sending contact email:', error);
      throw error;
    }
  },

  /**
   * Get portfolio links
   * @param {number} portfolioId - Portfolio ID
   * @param {boolean} activeOnly - Only fetch active links (default: true)
   * @returns {Promise<Array>} - Array of portfolio links
   */
  getPortfolioLinks: async (portfolioId, options = {}) => {
    try {
      let activeOnly = true;
      let token = null;

      if (typeof options === 'boolean') {
        activeOnly = options;
      } else if (options && typeof options === 'object') {
        if (options.activeOnly !== undefined) {
          activeOnly = options.activeOnly;
        }
        if (options.token) {
          token = options.token;
        }
      }

      const params = activeOnly ? '?is_active=true' : '';
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };

      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/${portfolioId}/links${params}`,
        {
          method: 'GET',
          headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching portfolio links:', error);
      throw error;
    }
  },

  /**
   * Get portfolio link categories
   */
  getLinkCategories: async (token = null) => {
    try {
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };
      const response = await fetch(
        `${API_BASE_URL}/api/links/categories`,
        {
          method: 'GET',
          headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching link categories:', error);
      throw error;
    }
  },

  /**
   * Get portfolio link category types
   */
  getLinkCategoryTypes: async (token = null) => {
    try {
      const headers = token ? getHeaders(token) : { 'Content-Type': 'application/json' };
      const response = await fetch(
        `${API_BASE_URL}/api/links/category-types`,
        {
          method: 'GET',
          headers,
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching link category types:', error);
      throw error;
    }
  },

  /**
   * Create a new portfolio link
   */
  createPortfolioLink: async (linkData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/links`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(linkData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating portfolio link:', error);
      throw error;
    }
  },

  /**
   * Update an existing portfolio link
   */
  updatePortfolioLink: async (linkId, updateData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/links/${linkId}`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(updateData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating portfolio link ${linkId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a portfolio link
   */
  deletePortfolioLink: async (linkId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/links/${linkId}`,
        {
          method: 'DELETE',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error deleting portfolio link ${linkId}:`, error);
      throw error;
    }
  },

  /**
   * Update the display order of portfolio links
   */
  updatePortfolioLinksOrder: async (portfolioId, linkOrders, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/${portfolioId}/links/order`,
        {
          method: 'PUT',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify({ link_orders: linkOrders }),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating portfolio link order:', error);
      throw error;
    }
  },

  /**
   * Create or update a portfolio link text (language-specific name/description)
   */
  createPortfolioLinkText: async (linkId, textData, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/links/portfolios/links/${linkId}/texts`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(textData),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error(`Error updating portfolio link text for link ${linkId}:`, error);
      throw error;
    }
  },
};

export default portfolioApi;
